import { useEffect, useState } from 'react';
import type { CardBackLogoType } from '../types';

interface LogoProps {
  color: string;
  size?: number;
}

const defaultSize = 96; // ~1 inch at 96dpi

// Cache for fetched SVG content
const svgCache = new Map<string, string>();

/**
 * Normalizes a hex color to 6 characters (e.g., #fff -> #ffffff)
 */
function normalizeHexColor(hex: string): string {
  const h = hex.replace('#', '');
  if (h.length === 3) {
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`;
  }
  return hex;
}

/**
 * Recolors an SVG by replacing fill and stroke colors.
 */
function recolorSvg(svgContent: string, color: string): string {
  const normalizedColor = normalizeHexColor(color);
  let result = svgContent;
  
  // Replace fill="#000000" and similar patterns
  result = result.replace(/fill="#[0-9a-fA-F]{3,6}"/g, `fill="${normalizedColor}"`);
  result = result.replace(/fill:#[0-9a-fA-F]{3,6}/g, `fill:${normalizedColor}`);
  
  // Replace stroke colors
  result = result.replace(/stroke="#[0-9a-fA-F]{3,6}"/g, `stroke="${normalizedColor}"`);
  result = result.replace(/stroke:#[0-9a-fA-F]{3,6}/g, `stroke:${normalizedColor}`);
  
  // Handle CSS style blocks including CDATA sections (like in skull.svg)
  // Match .st0{fill:#000000;} pattern anywhere in the content
  result = result.replace(/\.st(\d+)\s*\{\s*fill:\s*#[0-9a-fA-F]{3,6}\s*;\s*\}/g, `.st$1{fill:${normalizedColor};}`);
  
  return result;
}

/**
 * Normalizes an SVG to use 100% width/height so it fits its container.
 * Strips hardcoded width/height attributes and ensures viewBox is preserved.
 */
function normalizeSvgSize(svgContent: string): string {
  // Replace width="800px" height="800px" (or any fixed dimensions) with 100%
  let result = svgContent.replace(/\s+width="[^"]*"/g, ' width="100%"');
  result = result.replace(/\s+height="[^"]*"/g, ' height="100%"');
  
  return result;
}

/**
 * External SVG logo component. Fetches SVG, recolors it, and renders inline.
 */
function ExternalLogo({ src, color, size = defaultSize }: { src: string; color: string; size?: number }) {
  const [svgContent, setSvgContent] = useState<string | null>(null);

  useEffect(() => {
    const cached = svgCache.get(src);
    if (cached) {
      setSvgContent(cached);
      return;
    }

    fetch(src)
      .then(res => res.text())
      .then(text => {
        svgCache.set(src, text);
        setSvgContent(text);
      })
      .catch(err => console.error('Failed to load SVG:', err));
  }, [src]);

  if (!svgContent) {
    // Placeholder while loading
    return <div style={{ width: size, height: size }} />;
  }

  // Normalize size, then recolor
  const normalizedSvg = normalizeSvgSize(svgContent);
  const coloredSvg = recolorSvg(normalizedSvg, color);
  
  return (
    <div
      style={{ width: size, height: size }}
      dangerouslySetInnerHTML={{ __html: coloredSvg }}
      className="external-logo"
    />
  );
}

// 1. D20 - External SVG
export function D20Logo({ color, size = defaultSize }: LogoProps) {
  return <ExternalLogo src="/logos/d20.svg" color={color} size={size} />;
}

// 2. Crossed Swords - External SVG
export function CrossedSwordsLogo({ color, size = defaultSize }: LogoProps) {
  return <ExternalLogo src="/logos/crossed-swords.svg" color={color} size={size} />;
}

// 3. Dragon - External SVG
export function DragonLogo({ color, size = defaultSize }: LogoProps) {
  return <ExternalLogo src="/logos/dragon.svg" color={color} size={size} />;
}

// 4. Spellbook - External SVG
export function SpellbookLogo({ color, size = defaultSize }: LogoProps) {
  return <ExternalLogo src="/logos/spellbook.svg" color={color} size={size} />;
}

// 5. Wizard Tower - External SVG
export function WizardTowerLogo({ color, size = defaultSize }: LogoProps) {
  return <ExternalLogo src="/logos/tower.svg" color={color} size={size} />;
}

// 6. Crystal Ball - External SVG
export function CrystalBallLogo({ color, size = defaultSize }: LogoProps) {
  return <ExternalLogo src="/logos/crystal-ball.svg" color={color} size={size} />;
}

// 7. Potion Bottle - External SVG
export function PotionBottleLogo({ color, size = defaultSize }: LogoProps) {
  return <ExternalLogo src="/logos/potion.svg" color={color} size={size} />;
}

// 8. Shield & Crest - External SVG
export function ShieldCrestLogo({ color, size = defaultSize }: LogoProps) {
  return <ExternalLogo src="/logos/shield.svg" color={color} size={size} />;
}

// 9. Arcane Runes - Circle of mystical symbols
export function ArcaneRunesLogo({ color, size = defaultSize }: LogoProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Outer circle */}
      <circle cx="50" cy="50" r="42" fill="none" stroke={color} strokeWidth="2" />
      {/* Inner circle */}
      <circle cx="50" cy="50" r="32" fill="none" stroke={color} strokeWidth="2" />
      {/* Central pentagram-ish shape */}
      <polygon
        points="50,20 62,42 88,42 68,58 76,82 50,68 24,82 32,58 12,42 38,42"
        fill="none"
        stroke={color}
        strokeWidth="2"
      />
      {/* Rune symbols around the ring */}
      <text x="50" y="12" textAnchor="middle" fill={color} fontSize="8" fontFamily="serif">ᚠ</text>
      <text x="88" y="52" textAnchor="middle" fill={color} fontSize="8" fontFamily="serif">ᚢ</text>
      <text x="74" y="90" textAnchor="middle" fill={color} fontSize="8" fontFamily="serif">ᚦ</text>
      <text x="26" y="90" textAnchor="middle" fill={color} fontSize="8" fontFamily="serif">ᚨ</text>
      <text x="12" y="52" textAnchor="middle" fill={color} fontSize="8" fontFamily="serif">ᚱ</text>
      {/* Center eye */}
      <circle cx="50" cy="50" r="6" fill="none" stroke={color} strokeWidth="2" />
      <circle cx="50" cy="50" r="2" fill={color} />
    </svg>
  );
}

// 10. Skull & Bones - External SVG
export function SkullBonesLogo({ color, size = defaultSize }: LogoProps) {
  return <ExternalLogo src="/logos/skull.svg" color={color} size={size} />;
}

// Main logo renderer component
export function CardBackLogo({ type, color, size = defaultSize }: { type: CardBackLogoType; color: string; size?: number }) {
  const logoComponents: Record<CardBackLogoType, React.FC<LogoProps>> = {
    'd20': D20Logo,
    'crossed-swords': CrossedSwordsLogo,
    'dragon': DragonLogo,
    'spellbook': SpellbookLogo,
    'wizard-tower': WizardTowerLogo,
    'crystal-ball': CrystalBallLogo,
    'potion-bottle': PotionBottleLogo,
    'shield-crest': ShieldCrestLogo,
    'arcane-runes': ArcaneRunesLogo,
    'skull-bones': SkullBonesLogo,
  };

  const LogoComponent = logoComponents[type];
  return <LogoComponent color={color} size={size} />;
}
